/**
 * HTTP client for the backend.
 *
 * Layer rule: this module is pure logic — it never imports React and never
 * touches styling. Everything the UI needs from the server goes through here so
 * there is exactly one place that knows about URLs, credentials and error
 * shapes.
 *
 * 分层规则：本模块是纯逻辑，不引入 React，也不涉及任何样式。UI 需要的服务端数据
 * 一律走这里，保证「知道 URL、凭据和错误结构」的地方只有一处。
 */

/** Base path for all API calls. Vite proxies this in dev. API 根路径，开发时由 Vite 代理。 */
const API_BASE = '/api'

/**
 * How many consecutive network failures on one slice before giving up.
 *
 * 同一个分片上连续多少次网络失败才放弃。
 */
const MAX_CHUNK_ATTEMPTS = 5

/**
 * Pause before retrying a slice, long enough for a flapping link to settle.
 *
 * 重试一个分片前的等待，长到足够让一条时断时续的链路稳定下来。
 */
const CHUNK_RETRY_MS = 2000

/** Thrown for any non-2xx response, carrying the server's message. 非 2xx 响应抛出，带服务端消息。 */
export class ApiError extends Error {
  /**
   * @param {string} message Human-readable message / 可读消息。
   * @param {number} status HTTP status code / HTTP 状态码。
   * @param {object} [payload] Parsed response body / 已解析的响应体。
   */
  constructor(message, status, payload = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

/**
 * Perform a JSON request against the API.
 *
 * `credentials: 'include'` keeps the session cookie flowing; we never store a
 * token in localStorage, so an XSS cannot lift the session.
 *
 * 发起 JSON 请求。credentials: 'include' 保证会话 cookie 随行；我们不把 token 放进
 * localStorage，因此 XSS 无法窃取会话。
 *
 * @param {string} path Path below /api, e.g. '/content/news' / /api 之下的路径。
 * @param {object} [options] Fetch options plus `body` as a plain object / fetch 选项，body 传普通对象。
 * @returns {Promise<any>} Parsed JSON body / 解析后的 JSON。
 * @throws {ApiError} When the response is not 2xx / 响应非 2xx 时抛出。
 */
async function request(path, options = {}) {
  const { body, headers, ...rest } = options
  const init = {
    credentials: 'include',
    headers: { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...headers },
    ...rest,
  }
  if (body !== undefined) init.body = JSON.stringify(body)

  let response
  try {
    response = await fetch(`${API_BASE}${path}`, init)
  } catch (cause) {
    // Network-level failure: the dev server is probably not running.
    // 网络层失败：多半是后端没起来。
    throw new ApiError('无法连接后端服务 / Cannot reach the backend service', 0, { cause: String(cause) })
  }

  const text = await response.text()
  let payload = {}
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = { message: text }
    }
  }

  if (!response.ok) {
    throw new ApiError(payload.message || `请求失败 / Request failed (${response.status})`, response.status, payload)
  }
  return payload
}

// ---------------------------------------------------------------------------
// Auth / 认证
// ---------------------------------------------------------------------------

export const authApi = {
  /**
   * Exchange credentials for a session cookie.
   *
   * 用账号密码换取会话 cookie。
   *
   * @param {string} username Username / 用户名。
   * @param {string} password Password / 密码。
   * @returns {Promise<{user: object}>} The signed-in user / 已登录用户。
   */
  login: (username, password) => request('/auth/login', { method: 'POST', body: { username, password } }),

  /** Drop the session cookie. 清除会话 cookie。 @returns {Promise<object>} */
  logout: () => request('/auth/logout', { method: 'POST' }),

  /** Who am I? Returns `{user: null}` when signed out. 当前用户，未登录返回 null。 @returns {Promise<{user: object|null}>} */
  me: () => request('/auth/me'),

  /**
   * Change your own password.
   *
   * 修改自己的密码。
   *
   * @param {string} currentPassword Current password / 当前密码。
   * @param {string} newPassword New password / 新密码。
   * @returns {Promise<object>} Result / 结果。
   */
  changePassword: (currentPassword, newPassword) =>
    request('/auth/password', { method: 'POST', body: { currentPassword, newPassword } }),
}

// ---------------------------------------------------------------------------
// Users (admin only) / 用户管理（仅管理员）
// ---------------------------------------------------------------------------

export const usersApi = {
  /** List all accounts. 列出所有账号。 @returns {Promise<{items: object[]}>} */
  list: () => request('/admin/users'),

  /**
   * Create an editor or admin account.
   *
   * 创建编辑或管理员账号。
   *
   * @param {object} payload `{username, displayName, role, password}` / 账号信息。
   * @returns {Promise<object>} Created user / 新建用户。
   */
  create: (payload) => request('/admin/users', { method: 'POST', body: payload }),

  /**
   * Update an account (display name, role, active flag).
   *
   * 更新账号（显示名、角色、启用状态）。
   *
   * @param {string} id User id / 用户 id。
   * @param {object} payload Partial user / 部分字段。
   * @returns {Promise<object>} Updated user / 更新后的用户。
   */
  update: (id, payload) => request(`/admin/users/${id}`, { method: 'PATCH', body: payload }),

  /** Delete an account. 删除账号。 @param {string} id User id / 用户 id。 @returns {Promise<object>} */
  remove: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),

  /**
   * Force-set someone's password.
   *
   * 强制重置某人的密码。
   *
   * @param {string} id User id / 用户 id。
   * @param {string} password New password / 新密码。
   * @returns {Promise<object>} Result / 结果。
   */
  resetPassword: (id, password) => request(`/admin/users/${id}/password`, { method: 'POST', body: { password } }),
}

// ---------------------------------------------------------------------------
// API keys / API 密钥
// ---------------------------------------------------------------------------

/**
 * Personal API keys — the credential an MCP client authenticates with.
 *
 * Two different things are decided about a key, in two different places, and
 * every call below leans on the distinction. Its OWNER settles attribution: a
 * draft written through the key is created by the person who minted it. The KEY
 * settles capability: agent permissions, always, even when its owner is an
 * admin. So nothing here ever sends a role, and there is deliberately no method
 * that could raise one — the only field a caller supplies is a name.
 *
 * 个人 API 密钥 —— MCP 客户端据以认证的凭据。
 *
 * 关于一把密钥，有两件事分别由两处决定，下面每个方法都依赖这个区分。
 * 「所属人」决定署名：通过这把密钥写出的草稿，作者就是签发它的那个人。
 * 「密钥本身」决定权限：永远是 agent 权限，哪怕它的所属人是管理员。
 * 所以这里从不发送角色，也刻意没有任何一个方法能抬高它——调用方能提供的只有名称。
 */
export const keysApi = {
  /**
   * List keys.
   *
   * Defaults to the caller's own. `scope: 'all'` asks for every key on the site
   * and is answered only for a caller holding keys:readAll; the console hides
   * that section from everyone else, but hiding is presentation and the refusal
   * belongs to the server.
   *
   * 列出密钥。默认只列调用方自己的。scope='all' 请求站点上的全部密钥，
   * 只有持有 keys:readAll 的调用方才会得到回应；控制台会对其他人隐藏那一块，
   * 但隐藏只是展示层，真正的拒绝在服务端。
   *
   * @param {{scope?: 'mine'|'all'}} [options] Read scope / 读取范围。
   * @returns {Promise<{items: object[]}>} Key rows, never the secret / 密钥记录，绝不含明文。
   */
  list: (options = {}) => request(`/admin/keys${options.scope === 'all' ? '?scope=all' : ''}`),

  /**
   * Mint a key for the signed-in account.
   *
   * The plaintext comes back exactly once, in `key`; the server stores only a
   * digest of it, so this response is the single moment the secret exists
   * outside the client that will use it. A caller that drops it mints another.
   *
   * The server sends the stored row flat with `key` and `notice` beside it, and
   * they are pulled apart here rather than in the page. `item` is what a table
   * puts in its state; were the secret still riding along inside it, it would
   * follow that row into every list, re-render and devtools inspection for as
   * long as the page stays open — the one thing this design spends its effort
   * making short-lived.
   *
   * 为当前登录账号签发一把密钥。
   *
   * 明文只在这一次响应里出现（字段 `key`）；服务端只存它的摘要，因此这个响应是
   * 秘密在「将要使用它的那个客户端」之外唯一存在的一瞬。丢了就只能再签一把。
   *
   * 服务端把落库记录平铺返回，`key` 与 `notice` 并列其旁；拆开的动作放在这里，
   * 而不是放在页面里。进表格 state 的是 `item`：秘密若还藏在它里面，就会跟着那一行
   * 进入之后的每一次列表、每一次重渲染、每一次 devtools 查看，只要页面还开着——
   * 而「让它尽快消失」正是整个设计花力气在做的事。
   *
   * @param {string} name Human label for where the key will live / 说明这把密钥放在哪的名称。
   * @returns {Promise<{key: string, notice: string, item: object}>} Plaintext, the
   *   server's warning, and the stored row / 明文、服务端的提示语，以及落库记录。
   */
  create: async (name) => {
    const body = { name }
    const { key, notice, ...item } = await request('/admin/keys', { method: 'POST', body })
    return { key, notice, item }
  },

  /**
   * Revoke a key.
   *
   * Immediate and final: the next call from any client still holding it fails.
   * What that key already wrote stays — revoking a credential is not retracting
   * the drafts written with it.
   *
   * 吊销一把密钥。立即且不可撤销：任何还拿着它的客户端下一次调用就会失败。
   * 它此前写下的内容仍然保留——吊销凭据不等于撤回用它写的草稿。
   *
   * @param {string} id Key id / 密钥 id。
   * @returns {Promise<object>} Result / 结果。
   */
  revoke: (id) => request(`/admin/keys/${id}`, { method: 'DELETE' }),
}

// ---------------------------------------------------------------------------
// Content / 内容
// ---------------------------------------------------------------------------

export const contentApi = {
  /**
   * Read a collection.
   *
   * Public callers get published records only; `scope: 'admin'` returns drafts
   * too and requires a session.
   *
   * 读取一个集合。公开调用只返回已发布记录；scope='admin' 连草稿一起返回，需登录。
   *
   * @param {string} collection Collection key / 集合键名。
   * @param {{scope?: 'public'|'admin'}} [options] Read scope / 读取范围。
   * @returns {Promise<{items: object[]}>} Records / 记录列表。
   */
  list: (collection, options = {}) =>
    request(`${options.scope === 'admin' ? '/admin' : ''}/content/${collection}`),

  /**
   * Read one record by id.
   *
   * 按 id 读取单条记录。
   *
   * @param {string} collection Collection key / 集合键名。
   * @param {string} id Record id / 记录 id。
   * @param {{scope?: 'public'|'admin'}} [options] Read scope / 读取范围。
   * @returns {Promise<object>} The record / 记录。
   */
  get: (collection, id, options = {}) =>
    request(`${options.scope === 'admin' ? '/admin' : ''}/content/${collection}/${id}`),

  /**
   * Create a record.
   *
   * 新建记录。
   *
   * @param {string} collection Collection key / 集合键名。
   * @param {object} record Record body / 记录内容。
   * @returns {Promise<object>} Created record / 新建的记录。
   */
  create: (collection, record) => request(`/admin/content/${collection}`, { method: 'POST', body: record }),

  /**
   * Replace a record.
   *
   * 覆盖更新记录。
   *
   * @param {string} collection Collection key / 集合键名。
   * @param {string} id Record id / 记录 id。
   * @param {object} record Record body / 记录内容。
   * @returns {Promise<object>} Updated record / 更新后的记录。
   */
  update: (collection, id, record) =>
    request(`/admin/content/${collection}/${id}`, { method: 'PUT', body: record }),

  /**
   * Delete a record.
   *
   * 删除记录。
   *
   * @param {string} collection Collection key / 集合键名。
   * @param {string} id Record id / 记录 id。
   * @returns {Promise<object>} Result / 结果。
   */
  remove: (collection, id) => request(`/admin/content/${collection}/${id}`, { method: 'DELETE' }),

  /**
   * Persist a new display order for a collection.
   *
   * 保存集合的新排序。
   *
   * @param {string} collection Collection key / 集合键名。
   * @param {string[]} ids Record ids in the desired order / 期望顺序的记录 id。
   * @returns {Promise<object>} Result / 结果。
   */
  reorder: (collection, ids) => request(`/admin/content/${collection}/reorder`, { method: 'POST', body: { ids } }),
}

// ---------------------------------------------------------------------------
// Site settings / 站点设置
// ---------------------------------------------------------------------------

export const siteApi = {
  /** Read global settings (public). 读取全局设置（公开）。 @returns {Promise<object>} */
  get: () => request('/site'),

  /**
   * Update global settings (admin only).
   *
   * 更新全局设置（仅管理员）。
   *
   * @param {object} payload Settings blob / 设置内容。
   * @returns {Promise<object>} Updated settings / 更新后的设置。
   */
  update: (payload) => request('/admin/site', { method: 'PUT', body: payload }),
}

// ---------------------------------------------------------------------------
// Media / 媒体
// ---------------------------------------------------------------------------

export const mediaApi = {
  /**
   * Upload a single file.
   *
   * Uses FormData, so no JSON Content-Type here — the browser sets the
   * multipart boundary itself.
   *
   * 上传单个文件。用 FormData，因此不设 JSON Content-Type，由浏览器自己写
   * multipart 边界。
   *
   * @param {File|Blob} file File to upload / 待上传文件。
   * @param {string} kind One of image|video|audio|doc / 上传分桶。
   * @param {{filename?: string, thumb?: Blob|null, onProgress?: (fraction: number) => void}} [options] Extras / 附加项。
   * @returns {Promise<{url: string, name: string, size: number, mime: string}>} Stored asset / 已存资源。
   */
  upload: (file, kind, options = {}) =>
    new Promise((resolve, reject) => {
      const form = new FormData()
      form.append('kind', kind)
      form.append('file', file, options.filename || file.name || 'upload')
      // Appended after the original, and that order is load-bearing: multer streams the
      // parts in the order they arrive, so the server has the main file in hand before it
      // has to decide whether the thumbnail beside it is worth keeping.
      // 排在原文件之后，而这个顺序是有承重作用的：multer 按到达顺序流式处理各部分，
      // 因此服务端在判断「旁边这张缩略图值不值得留」之前，主文件已经在手上了。
      if (options.thumb) form.append('thumb', options.thumb, 'thumb.webp')

      // XHR rather than fetch: fetch still has no upload-progress event, and a
      // 300 MB video upload needs a progress bar.
      // 用 XHR 而不是 fetch：fetch 至今没有上传进度事件，而 300MB 的视频必须有进度条。
      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${API_BASE}/admin/upload`)
      xhr.withCredentials = true

      if (options.onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) options.onProgress(event.loaded / event.total)
        })
      }
      xhr.addEventListener('load', () => {
        let payload = {}
        try {
          payload = JSON.parse(xhr.responseText || '{}')
        } catch {
          payload = { message: xhr.responseText }
        }
        if (xhr.status >= 200 && xhr.status < 300) resolve(payload)
        else reject(new ApiError(payload.message || `上传失败 / Upload failed (${xhr.status})`, xhr.status, payload))
      })
      xhr.addEventListener('error', () => reject(new ApiError('上传失败 / Upload failed', 0)))
      xhr.addEventListener('abort', () => reject(new ApiError('上传已取消 / Upload aborted', 0)))
      xhr.send(form)
    }),

  /**
   * Upload a file in slices, resuming after an interruption.
   *
   * A single POST cannot carry a gigabyte: nginx caps the request body, the
   * proxy gives up after five minutes, and one dropped connection at 95% costs
   * the whole transfer. Here the file is sent in pieces and the server's own
   * count of what it has decides where the next piece starts — so a retry after
   * a broken link resends one slice, not the file.
   *
   * The server is asked where to continue rather than being told, and its answer
   * comes from the length of the file it actually wrote. That is what makes a
   * resumed upload byte-exact: a client that assumed it knew the position could
   * skip or duplicate a slice, and the result would be a corrupt video that
   * uploaded without a single error.
   *
   * 分片上传，中断后可续传。
   *
   * 单次 POST 送不动一个 GB：nginx 限制请求体、代理五分钟就放弃，
   * 而在 95% 处断一次线代价是整份重来。这里把文件切片发送，由服务端自己数「收到了多少」
   * 来决定下一片从哪开始——所以断线后的重试重发的是一个分片，不是整个文件。
   *
   * 是「问」服务端从哪继续，而不是「告诉」它；而它的答案来自它实际写下的那个文件的长度。
   * 正是这一点让续传后的字节完全准确：一个自以为知道位置的客户端可能跳过或重复一个分片，
   * 结果会是一个上传过程毫无报错、内容却已损坏的视频。
   *
   * @param {File|Blob} file File to upload / 待上传文件。
   * @param {string} kind One of image|video|audio|doc / 上传分桶。
   * @param {{filename?: string, onProgress?: (fraction: number) => void}} [options] Extras / 附加项。
   * @returns {Promise<object>} Stored asset / 已存资源。
   */
  uploadResumable: async (file, kind, options = {}) => {
    const filename = options.filename || file.name || 'upload'
    const started = await request('/admin/upload/begin', {
      method: 'POST',
      body: { filename, size: file.size, mime: file.type || '', kind },
    })
    const { uploadId } = started
    const chunkBytes = started.chunkBytes || 8 * 1024 * 1024

    let offset = started.offset || 0
    let attemptsLeft = MAX_CHUNK_ATTEMPTS
    while (offset < file.size) {
      const slice = file.slice(offset, Math.min(offset + chunkBytes, file.size))
      let response
      try {
        response = await fetch(`${API_BASE}/admin/upload/session/${uploadId}?offset=${offset}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: slice,
        })
      } catch {
        // The link dropped mid-slice. Ask the server how much of it survived and
        // carry on from there; only a run of failures means the upload is really
        // over.
        // 分片传到一半断线了。问服务端这一片活下来多少，从那儿继续；
        // 只有连续多次失败才说明这次上传真的结束了。
        attemptsLeft -= 1
        if (attemptsLeft <= 0) {
          throw new ApiError('上传中断，重试多次仍失败 / Upload interrupted and retries failed', 0)
        }
        await new Promise((resolve) => {
          setTimeout(resolve, CHUNK_RETRY_MS)
        })
        const progress = await request(`/admin/upload/session/${uploadId}`)
        offset = progress.offset
        continue
      }

      if (response.status === 409) {
        // The server and this loop disagree about the position, and the server
        // is the one holding the file.
        // 服务端与这个循环对位置的看法不一致，而文件在服务端手上。
        offset = (await response.json()).offset
        continue
      }
      if (!response.ok) {
        let payload = {}
        try {
          payload = await response.json()
        } catch {
          payload = {}
        }
        throw new ApiError(
          payload.message || `上传失败 / Upload failed (${response.status})`,
          response.status,
          payload
        )
      }

      offset = (await response.json()).offset
      attemptsLeft = MAX_CHUNK_ATTEMPTS
      if (options.onProgress) options.onProgress(offset / file.size)
    }

    return request(`/admin/upload/session/${uploadId}/finish`, { method: 'POST' })
  },

  /** List uploaded assets. 列出已上传资源。 @returns {Promise<{items: object[]}>} */
  list: () => request('/admin/media'),

  /**
   * Delete an uploaded asset.
   *
   * 删除已上传资源。
   *
   * @param {string} id Asset id / 资源 id。
   * @returns {Promise<object>} Result / 结果。
   */
  remove: (id) => request(`/admin/media/${id}`, { method: 'DELETE' }),
}

// ---------------------------------------------------------------------------
// Visits / 访问统计
// ---------------------------------------------------------------------------

export const statsApi = {
  /**
   * Count this visit and read back the running total.
   *
   * One call rather than a count-then-read pair: the footer wants the number it
   * just contributed to, and a second round trip to fetch it would show a
   * figure one behind.
   *
   * 计入本次访问并读回累计总数。
   *
   * 用一次调用而不是「先计数再读取」：页脚要的正是它刚刚贡献过的那个数，
   * 再发一次请求去取，显示出来的会比实际少一次。
   *
   * @returns {Promise<{total: number}>} Running total / 累计访问量。
   */
  visit: () => request('/visit', { method: 'POST' }),

  /**
   * Full breakdown for the console. Admin only.
   *
   * 控制台用的完整明细。仅管理员。
   *
   * @returns {Promise<object>} Statistics / 统计数据。
   */
  summary: () => request('/admin/stats'),
}

export { request as rawRequest }
